import {
  IonButton, IonCard, IonCardContent, IonIcon,
} from '@ionic/react';
import { alertCircle, warningOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import routePaths from '../lib/routePaths';
import { useStoreActions, useStoreState } from '../store';
import './StormAlertModal.css';

const StormAlertModal: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const alert = useStoreState((s) => s.storm.alert);
  const dismissedVersions = useStoreState((s) => s.storm.dismissedVersions);
  const reopenSignal = useStoreState((s) => s.storm.reopenSignal);
  const incrementDismiss = useStoreActions((a) => a.storm.incrementDismiss);
  const locale = useStoreState((s) => s.translations.language);

  // Local visibility — closed for the session by tapping Ignore/Dismiss,
  // reopened by banner click (reopenSignal) or a new state_version arriving.
  const [visible, setVisible] = useState(true);
  // When the banner is tapped, bypass the "already-dismissed" check so the
  // modal returns even for approaching/aftermath. Cleared on next dismiss.
  const [bypassDismissCheck, setBypassDismissCheck] = useState(false);

  useEffect(() => {
    // New alert state from API or push → show modal, reset bypass
    setVisible(true);
    setBypassDismissCheck(false);
  }, [alert?.state_version]);

  useEffect(() => {
    // Banner click or app-foreground → re-show modal AND bypass dismiss check
    if (reopenSignal > 0) {
      setVisible(true);
      setBypassDismissCheck(true);
    }
  }, [reopenSignal]);

  if (!alert || alert.state === 'none' || !visible) return null;

  // Imminent: ALWAYS shows (life-safety). Dismiss closes the current popup
  // but it re-appears on next app open / foreground / banner tap.
  // Other states: shown until user dismisses once, then banner only —
  // unless a banner tap set the bypass flag (then show again).
  if (alert.state !== 'imminent' && !bypassDismissCheck) {
    const dismissals = dismissedVersions[alert.state_version] || 0;
    if (dismissals >= 1) return null;
  }

  const isSpanish = (locale || '').startsWith('es');
  const title = (isSpanish ? alert.title_es : alert.title_en)
    || intl.formatMessage({ id: `storm.${alert.state}.title` });
  const body = (isSpanish ? alert.body_es : alert.body_en)
    || intl.formatMessage({ id: `storm.${alert.state}.body` });

  const handleDismiss = (): void => {
    if (alert.state !== 'imminent') {
      incrementDismiss(alert.state_version);
    }
    setVisible(false);
    setBypassDismissCheck(false);
  };

  const navigateTo = (route: string): void => {
    handleDismiss();
    history.push(route);
  };
  const handleMoreInfo = (): void => {
    if (alert.state === 'aftermath') navigateTo(routePaths.public.stormResources);
    else navigateTo(routePaths.public.stormPrepare);
  };
  const handleEvacRoutes = (): void => {
    navigateTo(routePaths.public.stormEvac);
  };

  return (
    <div className={`storm-modal-backdrop storm-${alert.state}`}>
      <IonCard className="storm-modal-card">
        <IonCardContent>
          <div className="storm-modal-icon">
            <IonIcon
              icon={alert.state === 'aftermath' ? alertCircle : warningOutline}
            />
          </div>
          <h2 className="storm-modal-title">{title}</h2>
          <p className="storm-modal-body">{body}</p>
          <div className="storm-modal-actions">
            {alert.state === 'imminent' && (
              <IonButton color="warning" expand="block" onClick={handleEvacRoutes}>
                <FormattedMessage id="storm.evacRoutes" />
              </IonButton>
            )}
            <IonButton
              color={alert.state === 'aftermath' ? 'primary' : 'medium'}
              expand="block"
              onClick={handleMoreInfo}
            >
              <FormattedMessage
                id={alert.state === 'aftermath' ? 'storm.resources' : 'storm.moreInfo'}
              />
            </IonButton>
            <IonButton color="light" expand="block" onClick={handleDismiss}>
              <FormattedMessage
                id={alert.state === 'aftermath' ? 'storm.dismiss' : 'storm.ignore'}
              />
            </IonButton>
          </div>
        </IonCardContent>
      </IonCard>
    </div>
  );
};

export default StormAlertModal;
